import { Box, Grid, Stack, Typography } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { black, blue, divider } from "@/theme/colors";
import { useFormContext, useWatch } from "react-hook-form";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useEffect, useRef, useState } from "react";
import { useEnum } from "@/api/services/global/enum";
import { Select } from "@/components/elements/Select";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type SortableLanguageSkillItemProps = {
  itemId: string;
  index: number;
  totalItems: number;
  control: ReturnType<typeof useFormContext<TTalentForm>>["control"];
  filteredOptions: { label: string; value: string | number }[];
  onRemove: (index: number) => void;
  onAdd: (index: number) => void;
  canAdd: boolean;
};

const SortableLanguageSkillItem = ({
  itemId,
  index,
  totalItems,
  control,
  filteredOptions,
  onRemove,
  onAdd,
  canAdd,
}: SortableLanguageSkillItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: itemId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <InnerBox mt={3} p={2} pt={0} showDivider={false} borderColor={divider.high} bordered={true}>
        {totalItems > 1 && (
          <Stack direction="row" alignItems="center" mt={2} mb={1} spacing={0.5}>
            <Box {...attributes} {...listeners} sx={{ cursor: "grab", display: "flex", alignItems: "center" }}>
              <DragIndicatorIcon sx={{ color: black[80], fontSize: 24 }} />
            </Box>
            <Typography variant="body14Bold" color={black[30]}>
              語学{index + 1}
            </Typography>
          </Stack>
        )}

        {totalItems <= 1 && (
          <Stack mt={2} mb={1}>
            <Typography variant="body14Bold" color={black[30]}>
              語学{index + 1}
            </Typography>
          </Stack>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormField control={control} name={`languageSkills.${index}.language`} label="語学（日本語以外の言語）">
              <Select placeholder="選択してください" options={filteredOptions} />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name={`languageSkills.${index}.content`} label="語学（日本語以外の言語）の内容">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </InnerBox>

      <Stack direction="row" alignItems="center" spacing={3} mt={1}>
        {canAdd && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={() => onAdd(index)}>
            <AddCircleOutlineIcon sx={{ color: blue[80], fontSize: 18 }} />
            <Typography variant="cap12">語学を追加</Typography>
          </Stack>
        )}
        {totalItems > 1 && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer", width: "fit-content" }} onClick={() => onRemove(index)}>
            <ClearIcon sx={{ color: black[80], fontSize: 18, display: "flex" }} />
            <Typography variant="cap12">削除</Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export const TalentFormLanguageSkillsFields = () => {
  const { control, setValue, trigger, formState, clearErrors } = useFormContext<TTalentForm>();
  const { data: foreignLanguageOptions } = useEnum("EForeignLanguage");
  const MAX_OPTION = foreignLanguageOptions?.length;
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [renderKey, setRenderKey] = useState(0);

  const languageSkills = useWatch({ control, name: "languageSkills" }) || [{ language: null, content: null }];

  // Generate stable IDs for sortable items
  const itemIdsRef = useRef<string[]>([]);
  if (itemIdsRef.current.length !== languageSkills.length) {
    // Adjust IDs array to match languageSkills length
    const newIds = [...itemIdsRef.current];
    while (newIds.length < languageSkills.length) {
      newIds.push(`lang-${Date.now()}-${newIds.length}`);
    }
    itemIdsRef.current = newIds.slice(0, languageSkills.length);
  }

  const handleInsertField = (afterIndex: number) => {
    if (languageSkills.length < MAX_OPTION) {
      isAddingRef.current = true;
      const updated = [...languageSkills];
      updated.splice(afterIndex + 1, 0, { language: null, content: null });
      // Update IDs
      const newIds = [...itemIdsRef.current];
      newIds.splice(afterIndex + 1, 0, `lang-${Date.now()}-${afterIndex + 1}`);
      itemIdsRef.current = newIds;
      setValue("languageSkills", updated);
      setRenderKey((k) => k + 1);
    }
  };

  const handleRemoveField = (index: number) => {
    if (languageSkills.length > 1) {
      const value = languageSkills[index];
      if (!value || value.language === null || value.language === undefined) {
        clearErrors("languageSkills");
        const updated = languageSkills.filter((_, i) => i !== index);
        const newIds = itemIdsRef.current.filter((_, i) => i !== index);
        itemIdsRef.current = newIds;
        setValue("languageSkills", updated);
        setRenderKey((k) => k + 1);
      } else {
        setDeleteIndex(index);
        setIsDeleteModalOpen(true);
      }
    }
  };

  const confirmDelete = () => {
    if (deleteIndex !== null && languageSkills.length > 1) {
      clearErrors("languageSkills");
      const updated = languageSkills.filter((_, i) => i !== deleteIndex);
      const newIds = itemIdsRef.current.filter((_, i) => i !== deleteIndex);
      itemIdsRef.current = newIds;
      setValue("languageSkills", updated);
      setRenderKey((k) => k + 1);
    }
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  useEffect(() => {
    if (!languageSkills || languageSkills.length === 0) {
      setValue("languageSkills", [{ language: null, content: null }]);
    }
  }, [languageSkills, setValue]);

  const isAddingRef = useRef(false);
  useEffect(() => {
    if (isAddingRef.current) {
      isAddingRef.current = false;
      return;
    }
    if (formState.isSubmitted) {
      trigger("languageSkills");
    }
  }, [languageSkills, trigger, formState.isSubmitted]);

  const getFilteredOptions = (index: number, currentValue: string | number | null | undefined) => {
    if (!foreignLanguageOptions) return [];

    const selectedValues = languageSkills
      .map((value, i) => (i !== index && value?.language != null ? value.language.toString() : null))
      .filter((value): value is string => value !== null);

    return foreignLanguageOptions.filter((option) => {
      const optionValue = option.value.toString();
      if (currentValue != null && optionValue === currentValue.toString()) {
        return true;
      }
      return !selectedValues.includes(optionValue);
    });
  };

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = itemIdsRef.current.findIndex((id) => id === active.id);
    const newIndex = itemIdsRef.current.findIndex((id) => id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = [...languageSkills];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      const reorderedIds = [...itemIdsRef.current];
      const [movedId] = reorderedIds.splice(oldIndex, 1);
      reorderedIds.splice(newIndex, 0, movedId);
      itemIdsRef.current = reorderedIds;

      setValue("languageSkills", reordered);
      setRenderKey((k) => k + 1);
    }
  };

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={itemIdsRef.current} strategy={verticalListSortingStrategy}>
            {languageSkills.map((value, index) => {
              const filteredOptions = getFilteredOptions(index, value?.language);

              return (
                <SortableLanguageSkillItem
                  key={`${itemIdsRef.current[index]}-${renderKey}`}
                  itemId={itemIdsRef.current[index]}
                  index={index}
                  totalItems={languageSkills.length}
                  control={control}
                  filteredOptions={filteredOptions}
                  onRemove={handleRemoveField}
                  onAdd={handleInsertField}
                  canAdd={languageSkills.length < MAX_OPTION}
                />
              );
            })}
          </SortableContext>
        </DndContext>

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteIndex(null);
        }}
        onOk={confirmDelete}
        message="「語学」を削除してもよろしいですか？"
      />
    </>
  );
};
